
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import ApiKeysTable from './api-keys/ApiKeysTable';
import CreateApiKeyDialog from './api-keys/CreateApiKeyDialog';
import ApiSettingsManager from './ApiSettingsManager';
import ApiDocumentation from './api-keys/ApiDocumentation';

interface ApiKey {
  id: string;
  key_name: string;
  api_key: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
  rate_limit_per_hour: number;
  usage_count: number;
}

type ApiSettings = Tables<'api_settings'>;

const ApiKeysManager = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [settings, setSettings] = useState<ApiSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchApiKeys();
    fetchSettings();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        toast.error('Failed to fetch API keys');
        return;
      }

      setApiKeys(data || []);
    } catch (error) {
      toast.error('Error fetching API keys');
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('api_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        if (error.code === 'PGRST116') {
          await createDefaultSettings();
        } else {
          toast.error('Failed to fetch API settings');
        }
        return;
      }

      setSettings(data);
    } catch (error) {
      console.error('Error fetching API settings:', error);
      toast.error('Error fetching API settings');
    } finally {
      setIsLoading(false);
    }
  };

  const createDefaultSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('api_settings')
        .insert({
          auto_approve_enabled: true,
          auto_approve_threshold: 0.8
        })
        .select()
        .single();

      if (error) {
        toast.error('Failed to create default settings');
        return;
      }

      setSettings(data);
    } catch (error) {
      console.error('Error creating default settings:', error);
    }
  };

  const deleteApiKey = async (id: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', id);

      if (error) {
        toast.error('Failed to delete API key');
        return;
      }

      toast.success('API key deleted');
      fetchApiKeys();
    } catch (error) {
      toast.error('Error deleting API key');
    }
  };

  const toggleApiKeyStatus = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) {
        toast.error('Failed to update API key status');
        return;
      }

      toast.success(`API key ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchApiKeys();
    } catch (error) {
      toast.error('Error updating API key status');
    }
  };

  const handleSettingsUpdate = (updatedSettings: ApiSettings) => {
    setSettings(updatedSettings);
  };

  if (isLoading) {
    return <div>Loading API keys...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">API Keys Management</h2>
        <CreateApiKeyDialog onApiKeyCreated={fetchApiKeys} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
        </CardHeader>
        <CardContent>
          <ApiKeysTable 
            apiKeys={apiKeys}
            onDelete={deleteApiKey}
            onToggleStatus={toggleApiKeyStatus}
          />
        </CardContent>
      </Card>

      {settings && (
        <ApiSettingsManager 
          settings={settings}
          onSettingsUpdate={handleSettingsUpdate}
        />
      )}

      <ApiDocumentation />
    </div>
  );
};

export default ApiKeysManager;
